import yup from "yup";
import locale from "@bonliva-crm/locale";
import { pick } from "@bonliva-crm/common";
import {
  BookingGroupInput,
  BookingIntroInput,
  ContractType,
  CustomerType,
  LegalPersonType
} from "@graphql";

export type FormData = {
  AreaOfExpertise: string;
  CostCenter: string;
  ContractType: ContractType;
  Specializations: string[];
  HospitalRef: LabelValue;
  DepartmentsRefs: string[];
  Consultant: LabelValue;
  consultantContractId: string;
  customerContractId: string;
  subVersionId: string;
  Weeks: Date[];
  ProjectId: string;

  LegalPersonType: LegalPersonType;
  BusinessName: string;
  BusinessSocialSecurityNo: string;
  UpdateConsultantWithSnapshotData: boolean;

  bookingIntro?: Omit<BookingIntroInput, "required"> & {
    required?: "true" | "false" | "";
  };

  WorkRequestId?: string | null;
  callNumber?: string;
  isNationalProcurement: "true" | "false";
  authorizedOrderer?: string;
  isDiscount?: "true" | "false" | "";
  nationalProcurementTravelRequired?: "true" | "false";
  travelTemplateLevel?: string;
  travelInvoicingWeeks?: Date[];
};

export const initialValues: FormData = {
  AreaOfExpertise: "",
  consultantContractId: null,
  customerContractId: null,
  subVersionId: null,
  CostCenter: "",
  ContractType: null,
  Specializations: [],
  HospitalRef: null,
  DepartmentsRefs: [],
  Consultant: null,
  Weeks: [],
  ProjectId: "",
  LegalPersonType: null,
  BusinessName: "",
  BusinessSocialSecurityNo: "",
  UpdateConsultantWithSnapshotData: false,
  WorkRequestId: null,
  bookingIntro: {
    required: "",
    startDate: null,
    endDate: null,
    description: ""
  },
  callNumber: null,
  isNationalProcurement: null,
  authorizedOrderer: null,
  isDiscount: "",
  nationalProcurementTravelRequired: null,
  travelTemplateLevel: null,
  travelInvoicingWeeks: []
};

export const validationSchema = yup.object<FormData>({
  AreaOfExpertise: yup.string().required(locale.AreaOfExpertise).nullable(true),
  CostCenter: yup.string().nullable(true).required(locale.CostCenter),
  Specializations: yup.array(yup.string()),
  HospitalRef: yup.object().nullable(true).required(locale.Hospital) as yup.Schema<any>,
  DepartmentsRefs: yup.array(yup.string()),
  Consultant: yup.object().nullable(true).required(locale.Consultant) as yup.Schema<any>,
  consultantContractId: yup.string().nullable(true),
  customerContractId: yup.string().nullable(true),
  subVersionId: yup.string().nullable(true),
  ContractType: yup.string().nullable(true) as yup.Schema<ContractType>,
  Weeks: yup.array(yup.date()).min(1, locale.SelectWeeks),
  ProjectId: yup.string().nullable(true).required(locale.Project),
  LegalPersonType: yup
    .string()
    .oneOf(Object.values(LegalPersonType), locale["Employment type"])
    .nullable(true)
    .required(locale["Employment type"]) as yup.Schema<LegalPersonType>,
  BusinessName: yup.string().nullable(true),
  BusinessSocialSecurityNo: yup.string().nullable(true),
  UpdateConsultantWithSnapshotData: yup.boolean(),
  bookingIntro: yup.object<any>({
    required: yup
      .mixed()
      .oneOf(["true", "false"], `${locale.BookingIntro}?`)
      .required(`${locale.BookingIntro}?`)
  }),
  isNationalProcurement: yup.mixed(),
  isDiscount: yup.mixed().oneOf(["true", "false"], locale.TenPercentDiscountQuestion),
  nationalProcurementTravelRequired: yup.mixed(),
  travelTemplateLevel: yup.string().nullable(true),
  travelInvoicingWeeks: yup.array(yup.date()).nullable(true)
});

export function formDataToGql(data: FormData): BookingGroupInput {
  return {
    ...pick(
      data,
      "AreaOfExpertise",
      "BusinessName",
      "BusinessSocialSecurityNo",
      "ContractType",
      "LegalPersonType",
      "Specializations",
      "UpdateConsultantWithSnapshotData",
      "Weeks",
      "WorkRequestId",
      "consultantContractId",
      "customerContractId",
      "subVersionId",
      "callNumber",
      "travelInvoicingWeeks"
    ),
    CostCenter: data.CostCenter,
    consultantContractId: data.consultantContractId,
    customerContractId: data.customerContractId,
    subVersionId: data.subVersionId,
    HospitalRef: {
      Id: data.HospitalRef.value,
      Name: data.HospitalRef.label,
      Type: CustomerType.Hospital
    },
    DepartmentsRefs: data.DepartmentsRefs.map((x) => ({
      Id: x,
      Type: CustomerType.Department
    })),
    ConsultantId: data.Consultant.value,
    ProjectId: data.ProjectId,
    bookingIntro: {
      ...data.bookingIntro,
      required: data.bookingIntro.required !== "false"
    },
    isNationalProcurement:
      !!data.isNationalProcurement && data.isNationalProcurement !== "false",
    authorizedOrderer: data.authorizedOrderer,
    isDiscount: trueFalseToBoolean(data.isDiscount),
    nationalProcurementTravelRequired: trueFalseToBoolean(
      data.nationalProcurementTravelRequired
    ),
    travelZone: data.travelTemplateLevel
  };
}

export function yesNoToBoolean(value: "yes" | "no"): boolean {
  return value === "yes";
}

export function trueFalseToBoolean(value: "true" | "false" | ""): boolean | null {
  if (value === "") {
    return null;
  }

  return value === "true";
}
